import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/i18n/LanguageContext";
import { getRegistrationUrl, EVENT_ENDED } from "@/config";
import { navKeys, pathFor, parsePath } from "@/navConfig";

export const Header = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { key: activeKey } = parsePath(pathname);
  const solid = scrolled || activeKey !== "home";

  const visibleNavKeys = EVENT_ENDED
    ? navKeys.map((k) => (k === "tickets" ? "recap" : k))
    : navKeys;

  const switchLang = (code) => {
    setLang(code);
    navigate(`${pathFor(activeKey, code)}${search}`);
  };

  const homeSub = [
    { key: "summit", to: pathFor("about", lang), label: t.aboutPage.tabs.summit },
    { key: "why", to: `${pathFor("about", lang)}?tab=why`, label: t.aboutPage.tabs.why },
    { key: "audience", to: `${pathFor("about", lang)}?tab=audience`, label: t.aboutPage.tabs.audience },
    { key: "syba", to: `${pathFor("about", lang)}?tab=syba`, label: t.aboutPage.tabs.syba },
    ...(EVENT_ENDED ? [] : [{ key: "venue", to: `${pathFor("about", lang)}?tab=venue`, label: t.aboutPage.tabs.venue }]),
  ];

  const programSub = [
    { key: "summit", to: pathFor("program", lang), label: t.program.pageTabs.summit },
    { key: "speakers", to: `${pathFor("program", lang)}?tab=speakers`, label: t.program.pageTabs.speakers },
    { key: "roundtable", to: `${pathFor("program", lang)}?tab=roundtable`, label: t.program.pageTabs.roundtable },
    { key: "gala", to: `${pathFor("program", lang)}?tab=gala`, label: t.program.pageTabs.gala },
    { key: "case-studies", to: pathFor("caseStudies", lang), label: t.program.pageTabs.caseStudies },
  ];

  const dropdowns = {
    home: { pathKey: "home", sub: homeSub },
    program: { pathKey: "program", sub: programSub },
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid ? "bg-[#0A2060]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="container-page flex items-center justify-between h-20">
        <Link to={pathFor("home", lang)} className="flex items-center gap-3 group" data-testid="logo-btn">
          <img src="/obal-roku-logo.png" alt="Obal roku 2026" className="h-14 w-14 sm:h-16 sm:w-16 object-contain" />
          <span className="font-display font-bold tracking-tight text-white leading-none">
            OBAL ROKU<span className="block text-[10px] tracking-[0.3em] text-[#CEA26B] font-medium">SUMMIT 2026</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {visibleNavKeys.map((k) => {
            if (dropdowns[k]) {
              const dd = dropdowns[k];
              return (
                <div key={k} className="relative group">
                  <Link
                    to={pathFor(dd.pathKey, lang)}
                    data-testid={`nav-${k}`}
                    onClick={(e) => e.currentTarget.blur()}
                    className={`flex items-center gap-1 text-sm tracking-wide transition-colors ${
                      activeKey === dd.pathKey ? "text-[#CEA26B]" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {t.nav[k]}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                    <div className="bg-[#0A2060] border border-white/10 min-w-[260px] py-2 shadow-2xl">
                      {dd.sub.map((s) => (
                        <Link
                          key={s.key}
                          to={s.to}
                          data-testid={`nav-${k}-${s.key}`}
                          onClick={(e) => e.currentTarget.blur()}
                          className="block px-5 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={k}
                to={pathFor(k, lang)}
                data-testid={`nav-${k}`}
                className={`text-sm transition-colors tracking-wide ${
                  activeKey === k ? "text-[#CEA26B]" : "text-slate-300 hover:text-white"
                }`}
              >
                {t.nav[k]}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white border border-white/15 px-3 py-2 transition-colors"
            >
              <Globe className="h-4 w-4" />
              {languages.find((l) => l.code === lang)?.label}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#0A2060] border-white/10 text-white min-w-[8rem]">
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => switchLang(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className="focus:bg-white/10 focus:text-white cursor-pointer justify-between"
                >
                  {l.label}
                  {lang === l.code && <Check className="h-4 w-4 text-[#CEA26B]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!EVENT_ENDED && (
          <a
            href={getRegistrationUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-btn"
            className="hidden sm:inline-flex bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors"
          >
            {t.nav.cta}
          </a>
          )}

          <button
            className="lg:hidden text-white"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0A2060]/95 backdrop-blur-xl border-b border-white/10" data-testid="mobile-menu">
          <nav className="container-page py-6 flex flex-col gap-4">
            {visibleNavKeys.map((k) => {
              if (dropdowns[k]) {
                const dd = dropdowns[k];
                return (
                  <div key={k} className="flex flex-col gap-3">
                    <Link
                      to={pathFor(dd.pathKey, lang)}
                      onClick={() => setMobileOpen(false)}
                      className={`text-left tracking-wide transition-colors ${
                        activeKey === dd.pathKey ? "text-[#CEA26B]" : "text-slate-200 hover:text-white"
                      }`}
                      data-testid={`mobile-nav-${k}`}
                    >
                      {t.nav[k]}
                    </Link>
                    <div className="flex flex-col gap-2 pl-4 border-l border-white/15">
                      {dd.sub.map((s) => (
                        <Link
                          key={s.key}
                          to={s.to}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-nav-${k}-${s.key}`}
                          className="text-sm text-slate-400 hover:text-white transition-colors"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={k}
                  to={pathFor(k, lang)}
                  onClick={() => setMobileOpen(false)}
                  className={`text-left py-1 tracking-wide transition-colors ${
                    activeKey === k ? "text-[#CEA26B]" : "text-slate-200 hover:text-white"
                  }`}
                  data-testid={`mobile-nav-${k}`}
                >
                  {t.nav[k]}
                </Link>
              );
            })}
            {!EVENT_ENDED && (
            <a
              href={getRegistrationUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="mt-2 text-center bg-[#CEA26B] hover:bg-[#BE965D] text-[#01154F] px-5 py-3 text-sm font-semibold tracking-wide transition-colors"
            >
              {t.nav.cta}
            </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
